import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import type { CheckGridOverviewItem } from "./types";
import { overviewSignal, satisfiedFraction, satisfiedPercent, signalMeta } from "./utils/display";

type ProgressBarProps = {
  percent: number;
  /** Fill colour for the grid's signal (from `signalMeta().bar`). */
  barClassName: string;
  label: string;
  className?: string;
};

export function CheckgridProgressBar({ percent, barClassName, label, className }: ProgressBarProps) {
  return (
    <div
      className={cn("overflow-hidden rounded-full bg-muted-foreground/25", className)}
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div className={cn("h-full rounded-full transition-[width] duration-150 ease-out motion-reduce:transition-none", barClassName)} style={{ width: `${percent}%` }} />
    </div>
  );
}

function CountWithPercentTooltip({
  complete,
  applicable,
  percent,
  className
}: {
  complete: number;
  applicable: number;
  percent: number;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn("inline-flex cursor-default items-baseline gap-1.5 leading-none", className)}>
          <span className="whitespace-nowrap font-mono text-[13px] tabular-nums text-foreground">
            {complete}
            <span className="text-muted-foreground"> / {applicable}</span>
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">
        {percent}% complete
      </TooltipContent>
    </Tooltip>
  );
}

/** Header progress — horizontal bar with count; percent on hover. */
export function CheckgridHeaderProgress({
  complete,
  applicable,
  percent,
  barClassName
}: {
  complete: number;
  applicable: number;
  percent: number;
  barClassName: string;
}) {
  const label = applicable > 0 ? `${complete} of ${applicable} checks complete` : "Nothing to check";

  return (
    <div className="mt-2 inline-flex max-w-full items-center gap-3" title={label}>
      <div className="relative min-w-[7.5rem] flex-1 sm:min-w-[9rem]">
        <CheckgridProgressBar
          percent={applicable > 0 ? percent : 0}
          barClassName={barClassName}
          label={label}
          className="h-1.5 w-full"
        />
        {applicable > 0 && percent > 0 && percent < 100 && (
          <span
            aria-hidden
            className={cn("pointer-events-none absolute inset-y-0 w-3 rounded-full blur-[2px]", barClassName, "opacity-50")}
            style={{ left: `calc(${percent}% - 6px)` }}
          />
        )}
      </div>
      {applicable > 0 ? (
        <CountWithPercentTooltip complete={complete} applicable={applicable} percent={percent} />
      ) : (
        <span className="text-[12.5px] text-muted-foreground">Nothing to check</span>
      )}
    </div>
  );
}

export function CheckgridProgressMini({ grid }: { grid: CheckGridOverviewItem }) {
  const { complete, applicable } = satisfiedFraction(grid);
  const pct = satisfiedPercent(grid);
  const { bar } = signalMeta(overviewSignal(grid));

  if (applicable <= 0) return <div className="w-[140px] shrink-0" aria-hidden />;

  return (
    <div className="w-[140px] shrink-0 text-right">
      <CountWithPercentTooltip complete={complete} applicable={applicable} percent={pct} className="justify-end" />
      <CheckgridProgressBar percent={pct} barClassName={bar} label={`${complete} of ${applicable} complete`} className="mt-1 ml-auto h-[3px] w-[90px]" />
    </div>
  );
}
